import { yupResolver } from "@hookform/resolvers/yup"
import {
  Accordion,
  Avatar,
  Button,
  FileInput,
  Group,
  Input,
  LoadingOverlay,
  Radio,
  Select,
  Tabs,
  TextInput,
} from "@mantine/core"
import {
  IconArchive,
  IconArrowBackUp,
  IconDeviceFloppy,
  IconTrash,
  IconUpload,
} from "@tabler/icons-react"
import { Suspense, lazy, useContext, useEffect, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import * as Yup from "yup"
import { User } from "../../../models/user.model"
import InputMask from "react-input-mask"
import Confirmation from "../../../shared/component/confirmation/action-confirmation"
import { phoneRegEx, phoneRegExAll } from "../../../shared/utility/Tools/RegEx"
import { downloadUrlParser } from "../../../shared/utility/Tools/tools"
import countryCodes from "../../../shared/utility/countryCodes.json"
import {
  useActivateUserMutation,
  useArchiveUserMutation,
  useCreateUserMutation,
  useDeleteUserMutation,
  useLazyGetArchivedUserQuery,
  useLazyGetUserQuery,
  useRestoreUserMutation,
  useUpdateUserMutation,
  useUpdateUserProfileMutation,
} from "../store/user.query"

import { useId } from "@mantine/hooks"
import AuthContext from "../../../shared/auth/context/authContext"
import ActivityLogWrapperComponent from "../../../shared/component/ActivityLog/activityLog-wrapper-component"
import EmptyIcon from "../../../shared/component/empty-icon/empty-icon"
import { Constants } from "../../../shared/constants/Constants"
import { useTranslation } from "react-i18next"

export interface Props {
  editMode: string
}

const UserRoleComponent = lazy(() => import("./user-role-component"))

export default function NewUserComponent(props: Props) {
  const { editMode } = props
  const params = useParams()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const defaultValue: User = {
    id: "",
    enabled: false,
    profileImage: {
      path: "",
      originalname: "",
      mimetype: "",
      filename: "",
      size: 0,
    },
    //Main
    name: "",
    email: "",
    phoneNumber: "",
    gender: "male",
  }

  const nameRegEx =
    /^([A-Za-z\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s'\-]*)$/gi
  let schema = Yup.object().shape({
    name: Yup.string()
      .min(3, t("minValidation", { field: t("name"), count: 3 }))
      .max(100, t("maxValidation", { field: t("name"), count: 100 }))
      .matches(nameRegEx, "Remove invalid characters")
      .required(t("nameRequired")),
    email: Yup.string()
      .email(t("validEmail"))
      .required(t("requiredValidation", { field: t("name") })),
    phoneNumber: Yup.string()
      .nullable()
      .matches(phoneRegExAll, "Phone number is not valid"),
    gender: Yup.string()
      .oneOf(["male", "female"], t("genderRequired"))
      .required(t("genderRequired")),
  })

  const Auth = useContext(AuthContext)
  const [getUser, user] = useLazyGetUserQuery()
  const [getArchivedUser, archivedUser] = useLazyGetArchivedUserQuery()
  const [createUser, newUser] = useCreateUserMutation()
  const [updateUser, updateResponse] = useUpdateUserMutation()
  const [updateUserProfile, updateProfileResponse] =
    useUpdateUserProfileMutation()
  const [archiveUser, archiveResponse] = useArchiveUserMutation()
  const [activateUser, activateResponse] = useActivateUserMutation()
  const [restoreUser, restoreCall] = useRestoreUserMutation()
  const [deleteUser, deleteCall] = useDeleteUserMutation()

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm<User>({
    resolver: yupResolver(schema),
    mode: "all",
  })

  let profileSchema = Yup.object().shape({
    profileData: Yup.mixed().test(
      "fileSize",
      t("sizeValidation", { field: t("profileImage"), size: "2MB" }),
      (value: any) => {
        if (!value || value.length === 0) return true // attachment is optional
        changeProfile()
        return value.size <= 2000000
      },
    ),
  })

  const profileForm = useForm<any>({
    resolver: yupResolver(profileSchema),
    mode: "all",
  })

  const [previewProfile, setPreviewProfile] = useState<any>(null)
  const [data] = useState<any>(countryCodes)
  const id = useId()
  const e_id = useId()
  const [code, setCode] = useState("+251")
  const [e_code, setE_Code] = useState("+251")
  const [, setUserId] = useState("")
  const [profilePic, setProfilePic] = useState<any>()

  function resetForm() {
    reset(defaultValue)
  }

  useEffect(() => {
    if (editMode === "detail") {
      reset(defaultValue)

      profileForm.reset({ profileData: undefined })
      setPreviewProfile("")

      if (params?.type === "archived") {
        getArchivedUser(`${params?.id}`).then((response: any) => {
          setCode(data.filter((d: any) => d.name == "Ethiopia")[0].dial_code)
          reset(response.data)
          if (response?.data?.profileImage) {
            let data = {
              originalname: response?.data?.profileImage.originalname,
              path: response?.data?.profileImage.path,
              filename: response?.data?.profileImage.filename,
              mimetype: response?.data?.profileImage.mimetype,
            }
            setPreviewProfile(downloadUrlParser(data))
            setProfilePic(response.data.profileImage)
          }
          setUserId(response.data.id)
        })
      } else {
        getUser(`${params?.id}`).then((response: any) => {
          setCode("+251")
          reset(response.data)

          if (response?.data?.profileImage) {
            let data = {
              originalname: response?.data?.profileImage.originalname,
              path: response?.data?.profileImage.path,
              filename: response?.data?.profileImage.filename,
              mimetype: response?.data?.profileImage.mimetype,
            }
            setPreviewProfile(downloadUrlParser(data))
            setProfilePic(response.data.profileImage)
          }
          setUserId(response.data.id)
        })
      }
    } else {
      resetForm()
    }
  }, [params.id, editMode, getUser, updateUser, setPreviewProfile])

  function changeProfile() {
    try {
      const objectUrl = URL.createObjectURL(
        profileForm.getValues("profileData"),
      )
      setPreviewProfile(objectUrl)
      return () => URL.revokeObjectURL(objectUrl)
    } catch (e) {
      return null
    }
  }

  function onSubmit(data: any) {
    if (editMode === "new") {
      createUser({ ...data, phoneNumber: data.phoneNumber.replace(/\s+/g, "") })
    } else {
      updateUser({
        ...data,
        id: params.id,
        phoneNumber: data.phoneNumber.replace(/\s+/g, ""),
      })
    }
  }

  const onError = (errors: any, e: any) => console.log(errors, e)

  return (
    // <div className="h-full relative w-full dark:bg-gray-700 dark:text-white flex space-x-2 justify-center">
    <ActivityLogWrapperComponent
      title={
        editMode === "new" ? (
          t("new") + " " + t("user", { count: 1 })
        ) : (
          // user?.data?.name ?? ""
          <div className="h-full space-x-2 flex items-center">
            {profilePic && (
              <Avatar
                src={downloadUrlParser({
                  path: profilePic?.path,
                  filename: profilePic?.filename,
                  originalname: profilePic?.originalname,
                  mimetype: profilePic?.mimetype,
                })}
                alt="photo"
              />
            )}
            <div>{user?.data?.name}</div>
          </div>
        )
      }
      item={user}
      path={`/user/detail/${params?.type}/${params.id}`}
      id={params.id ?? ""}
    >
      <LoadingOverlay
        visible={
          editMode === "detail" && (user.isFetching || updateResponse.isLoading)
        }
      />

      <Tabs defaultValue="main">
        <Tabs.List>
          <Tabs.Tab value="main">{t("main")}</Tabs.Tab>
          {editMode === "detail" && (
            <>
              <Tabs.Tab value="role">{t("roleAndPermission")}</Tabs.Tab>
              {/* <Tabs.Tab value="profile">Profile</Tabs.Tab> */}
            </>
          )}
        </Tabs.List>

        <Tabs.Panel value="main" pt="xs">
          <div className="flex  justify-center h-full">
            <form
              onSubmit={handleSubmit(onSubmit, onError)}
              className="w-full gap-y-4 dark:text-white"
            >
              <Accordion
                defaultValue={["info", "address", "other"]}
                multiple
                variant="separated"
                classNames={{
                  control:
                    "bg-gray-800 bg-opacity-50 backdrop-filter backdrop-blur-lg shadow-lg rounded-xl border border-gray-700",
                }}
              >
                <Accordion.Item value="info">
                  <Accordion.Control>
                    {t("personalInformation")}
                  </Accordion.Control>
                  <Accordion.Panel className="bg-gray-800">
                    <div className="flex-col space-y-4">
                      <div className="flex w-full space-x-4  justify-between">
                        <TextInput
                          classNames={{ label: "dark:text-white" }}
                          className="w-full"
                          // disabled
                          error={errors.name && `${errors?.name?.message}`}
                          withAsterisk
                          placeholder={t("name")}
                          label={t("name")}
                          {...register("name")}
                        />
                        <TextInput
                          classNames={{ label: "dark:text-white" }}
                          className="w-full"
                          // disabled
                          error={errors.email && `${errors?.email?.message}`}
                          withAsterisk
                          placeholder="email@example.com"
                          label={t("email")}
                          {...register("email")}
                        />
                      </div>
                      <div className="flex w-full space-x-4 justify-between">
                        <div className="flex w-full">
                          <Select
                            radius={"xs"}
                            searchable
                            nothingFound={
                              <div className="fle w-full justify-center items-center">
                                <EmptyIcon />
                              </div>
                            }
                            onChange={(code) => {
                              code && setCode(code)
                            }}
                            classNames={{
                              input:
                                "border border-gray-400/70 border-r-0 rounded rounded-r-none",
                            }}
                            value={code}
                            label={t("code")}
                            data={data?.map((item: any) => ({
                              label: `${item.name} (${item.dial_code})`,
                              value: item.dial_code,
                              key: item.name,
                            }))}
                            maxDropdownHeight={400}
                          />
                          <Input.Wrapper
                            id={id}
                            className="w-full"
                            label={t("phoneNumber")}
                            required
                            error={
                              errors.phoneNumber &&
                              `${errors?.phoneNumber?.message}`
                            }
                          >
                            <Input
                              classNames={{ input: "rounded-l-none" }}
                              component={InputMask}
                              mask={`${code} 999 999 999`}
                              id={id}
                              placeholder={t("phoneNumber")}
                              {...register("phoneNumber")}
                            />
                          </Input.Wrapper>
                        </div>
                        <div className="px-4 w-full flex items-center justify-start">
                          <Radio.Group
                            className="flex space-x-4"
                            label={t("gender")}
                            withAsterisk
                          >
                            <Radio
                              value="male"
                              label={t("male")}
                              // disabled
                              {...register("gender")}
                            />
                            <Radio
                              value="female"
                              label={t("female")}
                              // disabled
                              {...register("gender")}
                            />
                            <span className="text-red-500 text-xs">
                              {errors.gender && errors.gender?.message}
                            </span>
                          </Radio.Group>
                        </div>
                      </div>
                    </div>
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>

              <div className="w-full flex justify-end items-center">
                <Group position="right" mt="xl">
                  {editMode === "new" && (
                    <>
                      <Button
                        classNames={{ label: "flex space-x-1 " }}
                        variant="default"
                        className="dark:text-white hover:bg-primary hover:dark:bg_dark_primary hover:text-white"
                        type="reset"
                        size="xs"
                      >
                        <span>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="fill-current h-6"
                            viewBox="0 0 32 32"
                          >
                            <path d="M18.90625 4.09375C18.101563 4.09375 17.265625 4.367188 16.625 4.9375L16.625 4.96875L16.59375 5L4.90625 16.59375C3.695313 17.804688 3.703125 19.777344 4.84375 21.0625L4.875 21.09375L4.90625 21.09375L10.90625 27.09375C11.511719 27.699219 12.320313 27.992188 13.125 28L28 28L28 26L16.5 26L27 15.5C28.265625 14.234375 28.304688 12.210938 27.09375 11L21.09375 5C20.488281 4.394531 19.710938 4.09375 18.90625 4.09375 Z M 18.875 6.125C19.195313 6.125 19.492188 6.210938 19.6875 6.40625L25.6875 12.40625C26.074219 12.792969 26.128906 13.558594 25.59375 14.09375L20.5625 19.125L12.90625 11.46875L17.96875 6.4375L18 6.40625C18.253906 6.195313 18.570313 6.125 18.875 6.125 Z M 11.46875 12.90625L19.125 20.5625L14.03125 25.65625C14.019531 25.664063 14.011719 25.679688 14 25.6875C13.484375 26.117188 12.691406 26.066406 12.3125 25.6875L6.34375 19.75C6.328125 19.730469 6.328125 19.707031 6.3125 19.6875C5.902344 19.171875 5.9375 18.375 6.3125 18Z" />
                          </svg>
                        </span>
                        <span>{t("reset")}</span>
                      </Button>
                      <Button
                        variant="filled"
                        className="bg-primary text-white"
                        type="submit"
                        classNames={{ label: "flex space-x-1" }}
                        size={"xs"}
                        loading={newUser.isLoading}
                      >
                        <span>
                          <IconDeviceFloppy size={22} strokeWidth={1.5} />
                        </span>
                        <span>{t("submit")}</span>
                      </Button>
                    </>
                  )}
                  {editMode === "detail" && (
                    <>
                      {(params?.type === "archived" &&
                        archivedUser?.data?.deletedAt) ||
                      (params?.type === "active" && user?.data?.deletedAt) ? (
                        <>
                          <Confirmation
                            type={"danger"}
                            message={t("permanentDeleteMessage")}
                            onYes={() => deleteUser(`${params?.id}`)}
                            header={t("permanentDeleteTitle")}
                          >
                            <Button
                              variant="filled"
                              loading={deleteCall.isLoading}
                              className={`bg-danger text-white flex space-x-2 items-center`}
                              type="button"
                              classNames={{ label: "flex space-x-1" }}
                              size={"xs"}
                            >
                              <IconTrash size={20} strokeWidth={1.5} />
                              <span>{t("delete")}</span>{" "}
                            </Button>
                          </Confirmation>
                          <Confirmation
                            type={"notify"}
                            message={t("restoreMessage")}
                            onYes={() => restoreUser(`${params?.id}`)}
                            header={t("restoreTitle")}
                          >
                            <Button
                              variant="filled"
                              loading={restoreCall.isLoading}
                              className={`bg-green-500 text-white flex space-x-2 items-center`}
                              type="button"
                              classNames={{ label: "flex space-x-1" }}
                              size={"xs"}
                            >
                              <IconArrowBackUp size={20} strokeWidth={1.5} />
                              <span>{t("restore")}</span>
                            </Button>
                          </Confirmation>
                        </>
                      ) : (
                        <>
                          {Auth.user.id !== user?.data?.id && (
                            <>
                              <Confirmation
                                type={"notify"}
                                message={t("archiveMessage")}
                                header={t("archiveTitle")}
                                result={"single"}
                                resultRequired={true}
                                selectorLabel={t("selectAReason")}
                                labelDescription={t("archiveWhy")}
                                options={Constants(t)?.UserArchiveReason?.map(
                                  (reason: string) => {
                                    return { label: reason, value: reason }
                                  },
                                )}
                                customInput={true}
                                yesText={t("archive")}
                                onYes={(data: string) => {
                                  archiveUser({
                                    id: `${params.id}`,
                                    reason: data,
                                  })
                                }}
                              >
                                <Button
                                  variant="filled"
                                  loading={archiveResponse.isLoading}
                                  className="bg-danger text-white"
                                  type="button"
                                  classNames={{ label: "flex space-x-1" }}
                                  size={"xs"}
                                >
                                  <span>
                                    <IconArchive size={22} strokeWidth={1.5} />
                                  </span>
                                  <span>{t("archive")}</span>
                                </Button>
                              </Confirmation>

                              <Confirmation
                                type={"notify"}
                                message={t("activateMessage", {
                                  action: user.data?.enabled
                                    ? t("deactivate")
                                    : t("activate"),
                                })}
                                onYes={() => activateUser(`${params.id}`)}
                                header={t("activateTitle", {
                                  action: user.data?.enabled
                                    ? t("deactivation")
                                    : t("activation"),
                                })}
                              >
                                <Button
                                  variant="filled"
                                  loading={activateResponse.isLoading}
                                  className={`${
                                    user.data?.enabled
                                      ? " bg-danger"
                                      : "bg-green-500"
                                  } text-white`}
                                  type="button"
                                  classNames={{ label: "flex space-x-1" }}
                                  size={"xs"}
                                >
                                  <span>
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="fill-current h-6"
                                      viewBox="0 0 48 48"
                                    >
                                      <path d="M23.5 5C17.16639 5 12 10.16639 12 16.5C12 18.130861 12.341389 19.689962 12.957031 21.099609 A 1.50015 1.50015 0 1 0 15.707031 19.900391C15.252673 18.860038 15 17.713139 15 16.5C15 11.78761 18.78761 8 23.5 8C28.21239 8 32 11.78761 32 16.5C32 17.08427 31.94138 17.652314 31.830078 18.201172 A 1.50015 1.50015 0 1 0 34.769531 18.798828C34.92023 18.055686 35 17.28573 35 16.5C35 10.16639 29.83361 5 23.5 5 z M 23.5 12C21.032499 12 19 14.032499 19 16.5L19 25.759766L18.138672 25.404297C14.483804 23.900444 10.334734 26.436466 10.005859 30.375 A 1.50015 1.50015 0 0 0 10.693359 31.765625L19.957031 37.667969C20.601036 38.078718 21.151526 38.620029 21.576172 39.255859L23.033203 41.441406 A 1.50015 1.50015 0 0 0 23.035156 41.441406C23.803886 42.591886 25.189849 43.186167 26.554688 42.945312L31.882812 42.005859C33.603893 41.703285 34.998876 40.422039 35.449219 38.734375 A 1.50015 1.50015 0 0 0 35.451172 38.726562L37.832031 29.576172C38.653863 26.49462 36.64066 23.318721 33.501953 22.748047L28 21.748047L28 16.5C28 14.032499 25.967501 12 23.5 12 z M 23.5 15C24.346499 15 25 15.653501 25 16.5L25 23 A 1.50015 1.50015 0 0 0 26.232422 24.476562L32.964844 25.699219C34.424137 25.964545 35.315668 27.370273 34.933594 28.802734 A 1.50015 1.50015 0 0 0 34.929688 28.8125L32.550781 37.960938C32.399124 38.529274 31.940201 38.949356 31.363281 39.050781 A 1.50015 1.50015 0 0 0 31.363281 39.052734L26.033203 39.992188C25.834042 40.027338 25.642567 39.944908 25.529297 39.775391L24.072266 37.591797C23.417016 36.610136 22.565912 35.77367 21.570312 35.138672 A 1.50015 1.50015 0 0 0 21.568359 35.138672L13.251953 29.835938C13.814585 28.352335 15.413607 27.528548 16.996094 28.179688L19.929688 29.386719 A 1.50015 1.50015 0 0 0 22 28L22 16.5C22 15.653501 22.653501 15 23.5 15 z" />
                                    </svg>
                                  </span>
                                  {user.data?.enabled ? (
                                    <span>{t("deactivate")}</span>
                                  ) : (
                                    <span>{t("activate")}</span>
                                  )}
                                </Button>
                              </Confirmation>
                              <Button
                                variant="filled"
                                className="bg-primary text-white"
                                type="submit"
                                classNames={{ label: "flex space-x-1" }}
                                size={"xs"}
                                loading={updateResponse.isLoading}
                              >
                                <span>
                                  <IconDeviceFloppy
                                    size={22}
                                    strokeWidth={1.5}
                                  />
                                </span>
                                <span>{t("update")}</span>
                              </Button>
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}
                </Group>
              </div>
            </form>
          </div>
        </Tabs.Panel>

        {editMode === "detail" && (
          <>
            <Tabs.Panel value="role">
              <UserRoleComponent />
            </Tabs.Panel>
            {/* <Tabs.Panel value="profile" pt="xs">
              <div className="flex  justify-center h-full">
                <form
                  onSubmit={handleSubmit(onSubmit, onError)}
                  className="w-full gap-y-4 dark:text-white"
                >
                  <div className="flex-col space-y-4">
                    <div className="flex w-full justify-center mt-2">
                      <Avatar
                        size="xl"
                        color="blue"
                        radius="xl"
                        src={previewProfile}
                        alt={user?.data?.name}
                      >
                        {user.data?.name.charAt(0) ?? "US"}
                      </Avatar>
                    </div>

                    <div className="flex w-full justify-between mt-2">
                      <div className="px-4 w-full flex items-center justify-start">
                        <Controller
                          control={profileForm.control}
                          name="profileData"
                          render={({ field }) => (
                            <FileInput
                              clearable
                              className="w-full"
                              error={
                                profileForm.formState.errors.profileData &&
                                `${profileForm.formState.errors.profileData?.message}`
                              }
                              label="Upload Profile"
                              accept="image/png,image/jpeg,image/jpg"
                              icon={<IconUpload size={14} />}
                              {...field}
                            />
                          )}
                        />
                      </div>
                    </div>

                    <div className="w-full flex justify-end mt-2 items-center">
                      <Group className={"px-4"} position="right" mt="xl">
                        <Button
                          classNames={{ label: "flex space-x-1 " }}
                          variant="default"
                          className="dark:text-white hover:bg-primary hover:dark:bg_dark_primary hover:text-white"
                          onClick={() => {
                            setPreviewProfile(user?.data?.profileImage?.path);
                            profileForm.reset();
                            // Reset file input text - form reset couldn't reset it!
                            const file: HTMLInputElement | null =
                              document.querySelector(".file");
                            if (file) file.value = "";
                          }}
                          size="xs"
                        >
                          <span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="fill-current h-6"
                              viewBox="0 0 32 32"
                            >
                              <path d="M18.90625 4.09375C18.101563 4.09375 17.265625 4.367188 16.625 4.9375L16.625 4.96875L16.59375 5L4.90625 16.59375C3.695313 17.804688 3.703125 19.777344 4.84375 21.0625L4.875 21.09375L4.90625 21.09375L10.90625 27.09375C11.511719 27.699219 12.320313 27.992188 13.125 28L28 28L28 26L16.5 26L27 15.5C28.265625 14.234375 28.304688 12.210938 27.09375 11L21.09375 5C20.488281 4.394531 19.710938 4.09375 18.90625 4.09375 Z M 18.875 6.125C19.195313 6.125 19.492188 6.210938 19.6875 6.40625L25.6875 12.40625C26.074219 12.792969 26.128906 13.558594 25.59375 14.09375L20.5625 19.125L12.90625 11.46875L17.96875 6.4375L18 6.40625C18.253906 6.195313 18.570313 6.125 18.875 6.125 Z M 11.46875 12.90625L19.125 20.5625L14.03125 25.65625C14.019531 25.664063 14.011719 25.679688 14 25.6875C13.484375 26.117188 12.691406 26.066406 12.3125 25.6875L6.34375 19.75C6.328125 19.730469 6.328125 19.707031 6.3125 19.6875C5.902344 19.171875 5.9375 18.375 6.3125 18Z" />
                            </svg>
                          </span>
                          <span>Reset</span>
                        </Button>

                        <Button
                          variant="filled"
                          className="bg-primary text-white"
                          classNames={{ label: "flex space-x-1" }}
                          size={"xs"}
                          loading={updateProfileResponse.isLoading}
                          onClick={() => {
                            if (profileForm.getValues("profileData")) {
                              SubmitProfile();
                            } else {
                              profileForm.setError("profileData", {
                                message: "Please select an image",
                              });
                            }
                          }}
                        >
                          <span>
                            <IconDeviceFloppy size={22} strokeWidth={1.5} />
                          </span>
                          <span>Submit</span>
                        </Button>
                      </Group>
                    </div>
                  </div>
                </form>
              </div>
            </Tabs.Panel> */}
          </>
        )}
      </Tabs>
    </ActivityLogWrapperComponent>
    // </div>
  )
}
